/**
 * The web Dashboard's tiles in one request (#2193 A):
 * `GET /api/v1/cockpit/summary/dashboard?as_of=<day>` answers one block per
 * source, each the body of the route the tile used to read or that route's
 * status and message. `block(key)` gives a tile the query fields it reads
 * (`data`, `error`, `isLoading`, `refetch`, ...), so one failed source shows
 * only that tile's error state.
 *
 * - A failed block retries alone (`?blocks=<key>`) and is merged into the same
 *   cache entry.
 * - The minute poll is one `counts_only=true` request while the tab is visible
 *   (`visiblePoll`), merged into the same entry without touching the full
 *   read's freshness.
 * - Always current: every visit and every return to the tab paints the cached
 *   answer at once and reads the full summary again behind it, and any
 *   successful save marks it stale (`lib/queryClient.ts`).
 * - A day change keeps the blocks that do not depend on the day on screen and
 *   shows the others as loading; never across organisations.
 *
 * The route is admin-only (cockpit group); `useDashboardSources` falls back to
 * the per-tile hooks for everyone else.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useAuthContext } from "@/contexts/AuthContext";
import { OpenAPI } from "@/api/core/OpenAPI";
import { endpointKey, endpointQueryString, keepPreviousWithinOrg, type EndpointParams } from "@/lib/queryKeys";
import { visiblePoll } from "@/lib/polling";
import { retryUnlessClientError } from "@/lib/queryRetry";
import { fetchCockpit } from "@/hooks/useCockpit";
import type { OrgCapacityOverviewResponse, OrgCapacityRangeResponse, CapacityAttentionResponse } from "@/types/capacity";
import type { CoverageRangeResponse, UnderstaffedResponse } from "@/types/coverage";
import type { workflows_WorkflowItemStatsResponse } from "@/api/models/workflows_WorkflowItemStatsResponse";
import type { reconciliation_DebtorAccountsSummaryResponse } from "@/api/models/reconciliation_DebtorAccountsSummaryResponse";
import type { birthdays_UpcomingBirthdaysResponse } from "@/api/models/birthdays_UpcomingBirthdaysResponse";
import type { settlingin_PlanListResponse } from "@/api/models/settlingin_PlanListResponse";
import type { changes_ChangesFeedSummaryResponse } from "@/api/models/changes_ChangesFeedSummaryResponse";
import type { models_CalendarEvent } from "@/api/models/models_CalendarEvent";
import type { activities_ListActivitiesResponse } from "@/api/models/activities_ListActivitiesResponse";
import type { billing_ExpiringBFListResponse } from "@/api/models/billing_ExpiringBFListResponse";
import type { shifts_LiveCoverageResponse } from "@/api/models/shifts_LiveCoverageResponse";
import type { group_presence_OpenGroupPresenceResponse } from "@/api/models/group_presence_OpenGroupPresenceResponse";
import type { shifts_ShiftListResponse } from "@/api/models/shifts_ShiftListResponse";

export const DASHBOARD_SUMMARY_PATH = "/api/v1/cockpit/summary/dashboard";
/** How often the Dashboard's counts refresh while the tab is visible. */
export const DASHBOARD_POLL_MS = 60 * 1000;
const FULL_STALE_MS = 5 * 60 * 1000;

/** The body of each block: the route the tile read before (`summary_surfaces.go`). */
export interface DashboardSummaryBlocks {
  capacity: OrgCapacityOverviewResponse;
  occupancy_range: OrgCapacityRangeResponse;
  coverage: CoverageRangeResponse;
  workflow_stats: workflows_WorkflowItemStatsResponse;
  debtor_summary: reconciliation_DebtorAccountsSummaryResponse;
  birthdays: birthdays_UpcomingBirthdaysResponse;
  settling_in: settlingin_PlanListResponse;
  changes: changes_ChangesFeedSummaryResponse;
  /** `{events: [...]}` on the wire, though the generated client types it as a list. */
  events: models_CalendarEvent[] | { events?: models_CalendarEvent[] };
  activities: activities_ListActivitiesResponse;
  understaffed_ahead: UnderstaffedResponse;
  capacity_attention: CapacityAttentionResponse;
  bf_expiring: billing_ExpiringBFListResponse;
  /** The live staffing card (today only). */
  live_coverage: shifts_LiveCoverageResponse;
  presence: group_presence_OpenGroupPresenceResponse;
  shifts: shifts_ShiftListResponse;
}

export type DashboardBlockKey = keyof DashboardSummaryBlocks;

/** Every default block of the surface, in the server's order. */
const ALL_BLOCKS: readonly DashboardBlockKey[] = [
  "capacity",
  "occupancy_range",
  "coverage",
  "workflow_stats",
  "debtor_summary",
  "birthdays",
  "settling_in",
  "changes",
  "events",
  "activities",
  "understaffed_ahead",
  "capacity_attention",
  "bf_expiring",
  "live_coverage",
  "presence",
  "shifts",
];

/** The blocks whose answer depends on the selected day. */
const DATED_BLOCKS: ReadonlySet<DashboardBlockKey> = new Set<DashboardBlockKey>([
  "capacity",
  "occupancy_range",
  "coverage",
  "events",
  "activities",
  "understaffed_ahead",
  "shifts",
]);

export interface SummaryBlockError {
  status: number;
  message: string;
  code?: string;
}

export interface SummaryBlock<T> {
  data?: T;
  error?: SummaryBlockError;
}

export interface DashboardSummaryResponse {
  surface: string;
  as_of: string;
  from: string;
  to: string;
  generated_at: string;
  blocks: { [K in DashboardBlockKey]?: SummaryBlock<DashboardSummaryBlocks[K]> };
}

/** The query fields a tile reads, for one source. */
export interface BlockSource<T> {
  data: T | undefined;
  error: unknown;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isFetching: boolean;
  /** Re-reads this source only. */
  refetch: () => void;
}

/** A failed block as an error the error states read (`status`, the server's message). */
function blockError(error: SummaryBlockError): Error & { status: number; body: { message: string; code?: string } } {
  return Object.assign(new Error(error.message), {
    status: error.status,
    body: { message: error.message, ...(error.code ? { code: error.code } : {}) },
  });
}

type SummaryBlocks = DashboardSummaryResponse["blocks"];

function mergeBlocks(old: DashboardSummaryResponse | undefined, blocks: SummaryBlocks): DashboardSummaryResponse | undefined {
  return old ? { ...old, blocks: { ...old.blocks, ...blocks } } : old;
}

export interface DashboardSummaryOptions {
  asOf: string;
  enabled: boolean;
  /**
   * Cache entries a block's answer is written into, for a widget that keeps
   * its own query (its own invalidation after an edit on the Dashboard).
   */
  seeds?: { [K in DashboardBlockKey]?: QueryKey };
  /** Blocks the user may not read (no module, reconciliation off): not requested. */
  skip?: readonly DashboardBlockKey[];
}

export function useDashboardSummary({ asOf, enabled, seeds, skip = [] }: DashboardSummaryOptions) {
  const { isSignedIn, isAuthReady, getToken, orgId } = useAuthContext();
  const queryClient = useQueryClient();
  const active = enabled && !!isSignedIn && isAuthReady && !!orgId && !!asOf;

  // The default set needs no list; a narrower one names its blocks.
  const blocks = skip.length > 0 ? ALL_BLOCKS.filter((key) => !skip.includes(key)).join(",") : undefined;
  const fullParams = { as_of: asOf, blocks };
  const fullKey = endpointKey(orgId, DASHBOARD_SUMMARY_PATH, fullParams);
  const countsKey = endpointKey(orgId, DASHBOARD_SUMMARY_PATH, { as_of: asOf, counts_only: true });

  const read = useCallback(
    async (params: EndpointParams, signal?: AbortSignal) => {
      const token = await getToken();
      const query = endpointQueryString(DASHBOARD_SUMMARY_PATH, params);
      return fetchCockpit<DashboardSummaryResponse>(
        `${OpenAPI.BASE}${DASHBOARD_SUMMARY_PATH}?${query}`,
        token,
        signal ?? new AbortController().signal,
      );
    },
    [getToken],
  );

  /** Writes blocks into the full read without making it look fresher than it is. */
  const merge = useCallback(
    (key: QueryKey, blocks: SummaryBlocks) => {
      const updatedAt = queryClient.getQueryState(key)?.dataUpdatedAt;
      queryClient.setQueryData<DashboardSummaryResponse>(key, (old) => mergeBlocks(old, blocks), { updatedAt });
    },
    [queryClient],
  );

  // The latest seeds, read by the query function without being part of its identity.
  const seedKeys = useRef(seeds);
  seedKeys.current = seeds;

  const query = useQuery<DashboardSummaryResponse>({
    queryKey: fullKey,
    queryFn: async ({ signal }) => {
      const data = await read(fullParams, signal);
      // The counts are fresh too: the poll waits a full interval.
      queryClient.setQueryData(countsKey, Date.now());
      for (const [key, seedKey] of Object.entries(seedKeys.current ?? {}) as [DashboardBlockKey, QueryKey][]) {
        const entry = data.blocks[key];
        if (entry && entry.data !== undefined && !entry.error) queryClient.setQueryData(seedKey, entry.data);
      }
      return data;
    },
    enabled: active,
    placeholderData: keepPreviousWithinOrg(orgId),
    staleTime: FULL_STALE_MS,
    // Always current (#2193): a return to the Dashboard or to the tab paints the
    // cached answer at once and reads again behind it. Any save elsewhere marks
    // it stale too (lib/queryClient.ts).
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    retry: (count, error) => retryUnlessClientError(count, error, { maxRetries: 1 }),
  });

  const fullReady = query.isSuccess && !query.isPlaceholderData;
  // Its data is the time of the latest read, full or counts (the full read sets it too).
  const counts = useQuery<number>({
    queryKey: countsKey,
    queryFn: async ({ signal }) => {
      const fresh = await read({ as_of: asOf, counts_only: true }, signal);
      merge(fullKey, fresh.blocks);
      return Date.now();
    },
    enabled: active && fullReady,
    staleTime: DASHBOARD_POLL_MS,
    ...visiblePoll(DASHBOARD_POLL_MS),
    retry: false,
  });

  // Blocks being re-read on their own (a tile's retry).
  const [rereading, setRereading] = useState<ReadonlySet<DashboardBlockKey>>(() => new Set());
  const latestKey = useRef<QueryKey>(fullKey);
  latestKey.current = fullKey;

  const refetchBlock = useCallback(
    async (key: DashboardBlockKey) => {
      setRereading((prev) => new Set(prev).add(key));
      try {
        merge(latestKey.current, (await read({ as_of: asOf, blocks: key })).blocks);
      } catch (error) {
        // The retry itself failed: the tile keeps its error state with the new cause.
        const status = (error as { status?: number }).status ?? 0;
        merge(latestKey.current, { [key]: { error: { status, message: (error as Error).message } } });
      } finally {
        setRereading((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [asOf, merge, read],
  );

  const { data, error, isLoading, isFetching, isPlaceholderData, refetch } = query;
  const block = useMemo(() => {
    const cache = new Map<DashboardBlockKey, BlockSource<unknown>>();
    return <K extends DashboardBlockKey>(key: K): BlockSource<DashboardSummaryBlocks[K]> => {
      const hit = cache.get(key);
      if (hit) return hit as BlockSource<DashboardSummaryBlocks[K]>;
      // Another day's answer is no answer for a tile that shows a day.
      const waiting = isPlaceholderData && DATED_BLOCKS.has(key);
      const entry = waiting ? undefined : data?.blocks?.[key];
      const busy = rereading.has(key);
      const failed = entry?.error ? blockError(entry.error) : data ? null : (error ?? null);
      const result: BlockSource<DashboardSummaryBlocks[K]> = {
        data: entry?.error ? undefined : (entry?.data as DashboardSummaryBlocks[K] | undefined),
        error: busy ? null : failed,
        isError: !busy && failed !== null,
        isLoading: isLoading || waiting || (busy && entry?.data === undefined),
        isSuccess: !busy && entry !== undefined && !entry.error,
        isFetching: isFetching || busy,
        // Without any answer the whole summary is read again.
        refetch: () => void (data ? refetchBlock(key) : refetch()),
      };
      cache.set(key, result);
      return result;
    };
  }, [data, error, isLoading, isFetching, isPlaceholderData, rereading, refetchBlock, refetch]);

  /** When the figures on screen were read (ms), 0 before the first answer. */
  const updatedAt = isPlaceholderData ? 0 : Math.max(query.dataUpdatedAt, counts.data ?? 0);

  return { block, query, updatedAt };
}
